/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import { faStar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import Head from "next/head";
import { Component } from "react";

import Banner from "../../components/banner";

// import AddToCartButton from "../../components/buttons/add-to-cart-button";
import Certifications from "../../components/certifications";

// import InStock from "../../components/in-stock";
import Newsletter from "../../components/newsletter";
import OpenGraphHead from "../../components/og-head";
import RelatedProducts from "../../components/related-products";
import { Slideshow } from "../../components/slideshow";
import SoldOut from "../../components/sold-out";
import TwitterHead from "../../components/twitter-head";
import CollectionKind from "../../enums/collection-kind";
import ProductKind from "../../enums/product-kind";
import { toAbsolutePageURL } from "../../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

export default class StrawberryCremeOrganicLipBalm extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render(): JSX.Element {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/products/strawberry-creme-organic-lip-balm",
                                           market);
    const alt = "Lasting Smiles";
    const title = "Strawberry Creme Organic Lip Balm · Lasting Smiles";
    const description = "Lasting Smiles Strawberry Creme Lip Balm. " +
      "Natural, organic, and fair trade. " +
      "No animal testing. " +
      "100% recycled plastic lip balm tube. " +
      "For soft, hydrated, and healthy lips. " +
      "Caring for people and our planet. " +
      "The world's most caring lip care. " +
      "Smile for a cause.";

    const list = {
      hidden: {},
      visible: {
        transition: {
          delay: 0.25,
          staggerChildren: 0.1,
          when: "beforeChildren",
        },
      },
    };

    const item = {
      hidden: {
        opacity: 0,
        scale: 0,
        y: 50,
      },
      visible: {
        opacity: 1,
        scale: 1,
        y: 0,
      },
    };

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/products/strawberry-creme-organic-lip-balm", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/products/strawberry-creme-organic-lip-balm", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/products/strawberry-creme-organic-lip-balm", "en-CA")}
            hrefLang="en-CA"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/products", market),
                    "name": "Products",
                    "position": 2,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/products/strawberry-creme-organic-lip-balm", market),
                    "name": "Strawberry Creme Organic Lip Balm",
                    "position": 3,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <OpenGraphHead
          alt={alt}
          description={description}
          slug="products-strawberry-creme-organic-lip-balm"
          title={title}
          type="website"
          url={canonicalUrl}
        />
        <TwitterHead
          alt={alt}
          description={description}
          slug="products-strawberry-creme-organic-lip-balm"
          title={title}
        />
        <Banner />
        <Newsletter />
        <section className="px-2 py-10 sm:px-5">
          <div className="max-w-screen-lg mx-auto">
            <h1 className="font-medium font-serif text-3xl text-center">
              Strawberry&nbsp;Creme Organic&nbsp;Lip&nbsp;Balm, 4&nbsp;Pack
            </h1>
            <p className="text-center text-xl">
              For soft, hydrated, and healthy lips.
            </p>
            <div className="flex flex-col sm:flex-row">
              <div className="flex-initial sm:max-w-xs">
                <Slideshow
                  imageAlts={[
                    "Strawberry Creme Organic Lip Balm 4 Pack",
                    "Strawberry Creme Organic Lip Balm Array",
                    "Strawberry Creme Organic Lip Balm Art",
                  ]}
                  imageJpegs={[
                    `${require("../../public/images/products/strawberry-creme-organic-lip-balm/pack.jpg")}`,
                    `${require("../../public/images/products/strawberry-creme-organic-lip-balm/array.jpg")}`,
                    `${require("../../public/images/products/strawberry-creme-organic-lip-balm/art.jpg")}`,
                  ]}
                  imageWebps={[
                    `${require("../../public/images/products/strawberry-creme-organic-lip-balm/pack.webp")}`,
                    `${require("../../public/images/products/strawberry-creme-organic-lip-balm/array.webp")}`,
                    `${require("../../public/images/products/strawberry-creme-organic-lip-balm/art.webp")}`,
                  ]}
                />
              </div>
              <div className="flex-1 m-5">
                <p className="text-xl sm:text-left text-center">
                  Smooth and soothe your lips with juicy strawberries and coffee bean antioxidant rich oil.
                  Made with 1st upcycled coffee oil derived from coffee grounds containing polyphenols and tocopherols.
                </p>
                <SoldOut />
                {/* <InStock />
                <AddToCartButton
                  collection={CollectionKind.LipBalm}
                  product={ProductKind.StrawberryCremeOrganicLipBalm}
                /> */}
                <Certifications collection={CollectionKind.LipBalm} />
              </div>
            </div>
          </div>
        </section>
        <section className="bg-strawberry-creme-300 px-2 py-10 sm:px-5">
          <div className="max-w-screen-lg mx-auto">
            <div className="text-center">
              <img
                alt="Strawberry Creme Icon"
                className="block h-10 mx-auto w-10"
                src={require("../../public/icons/flavors/strawberry-creme.svg") as string}
              />
              <h2 className="font-medium font-serif text-2xl">
                Certified&nbsp;Organic and Fair&nbsp;Trade Ingredients
              </h2>
            </div>
            <div className="flex flex-col flex-wrap sm:flex-row">
              <div className="flex-1 m-5">
                <h3 className="font-medium font-serif sm:text-left text-center text-xl">
                  Botanical Formula
                </h3>
                <ul className="sm:text-left text-center text-lg">
                  <li>Coconut Oil</li>
                  <li>Beeswax</li>
                  <li>Sunflower Seed Oil</li>
                  <li>Coffee Oil</li>
                  <li>Shea Butter</li>
                  <li>Flavor Oil</li>
                  <li>Cocoa Butter</li>
                  <li>Jojoba Oil</li>
                  <li>Vitamin E</li>
                </ul>
              </div>
              <div className="flex-1 m-5">
                <h3 className="font-medium font-serif sm:text-left text-center text-xl">
                  Ingredients
                </h3>
                <p className="sm:text-left text-center text-lg">
                  Cocos nucifera (Coconut) oil, Cera alba (Beeswax), Helianthus annuus (Sunflower) oil, Butyrospermum parkii (Shea) butter, Strawberry creme flavor, Theobroma cacao (Cocoa) butter, Coffea arabica (Coffee) seed oil, Simmondsia chinensis (Jojoba) oil, Tocopherol (Vitamin E).
                  Certified Organic, Certified Fair Trade
                </p>
              </div>
            </div>
          </div>
        </section>
        <RelatedProducts
          collection={CollectionKind.LipBalm}
          skipProduct={ProductKind.StrawberryCremeOrganicLipBalm}
        />
      </>
    );
  }
}
